var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDOMNode } from 'libs/dom';
import { forwardRef } from 'libs/react';
import { useEffect } from 'libs/react';
import { useState } from 'libs/react';
import { useValue } from 'libs/react';
import { wait } from 'libs/util/time';
import { React } from 'libs/react';
/**
 * @component PresentStack
 * @since 1.0.0
 */
export const PresentStack = forwardRef((props, ref) => {
    const { presentClassName, dismissClassName, disposeClassName, duration } = Object.assign({ disposeClassName: 'dispose', presentClassName: 'present', dismissClassName: 'dismiss', duration: 750 }, props);
    const self = useValue(() => ({
        queue: [],
        nodes: {},
        frame: 0,
        count: 0
    }));
    const [stack, setStack] = useState({});
    /**
     * @function play
     * @since 1.0.0
     * @hidden
     */
    function play() {
        return __awaiter(this, void 0, void 0, function* () {
            const operation = self.queue[0];
            if (operation == null || operation.playing) {
                return;
            }
            if (operation.type == 'present') {
                insertItem(operation);
            }
            operation.playing = true;
            yield waitForAnimationFrame();
            const presentNode = getNode(operation.present);
            const dismissNode = getNode(operation.dismiss);
            if (presentNode) {
                removeDisposeClasses(presentNode);
                appendPresentClasses(presentNode);
            }
            if (dismissNode) {
                removeDisposeClasses(dismissNode);
                appendDismissClasses(dismissNode);
            }
            yield wait(duration);
            if (presentNode) {
                removePresentClasses(presentNode);
            }
            if (dismissNode) {
                removeDismissClasses(dismissNode);
                appendDisposeClasses(dismissNode);
            }
            operation.playing = false;
            if (operation.type == 'dismiss') {
                removeItem(operation);
            }
            self.queue.shift();
            play();
        });
    }
    /**
     * @function present
     * @since 1.0.0
     * @hidden
     */
    function present(content) {
        self.count++;
        const index = self.count - 1;
        content = createItem(index, content);
        const operation = {
            type: 'present',
            present: index,
            dismiss: index - 1,
            content: content,
            playing: false
        };
        self.queue.push(operation);
        play();
    }
    /**
     * @function dismiss
     * @since 1.0.0
     * @hidden
     */
    function dismiss() {
        self.count--;
        const index = self.count;
        const operation = {
            type: 'dismiss',
            dismiss: index,
            present: index - 1,
            content: null,
            playing: false
        };
        self.queue.push(operation);
        play();
    }
    /**
     * @function createItem
     * @since 1.0.0
     * @hidden
     */
    function createItem(key, component) {
        function set(ref) {
            self.nodes[key] = ref;
        }
        return React.createElement(Item, { key: key, ref: set, item: component });
    }
    /**
     * @function insertItem
     * @since 1.0.0
     * @hidden
     */
    function insertItem(operation) {
        setStack((stack) => insert(stack, operation.present, operation.content));
    }
    /**
     * @function removeItem
     * @since 1.0.0
     * @hidden
     */
    function removeItem(operation) {
        setStack((stack) => remove(stack, operation.dismiss));
    }
    /**
     * @function waitForAnimationFrame
     * @since 1.0.0
     * @hidden
     */
    function waitForAnimationFrame() {
        return __awaiter(this, void 0, void 0, function* () {
            if (self.frame) {
                self.frame = cancelFrame(self.frame);
            }
            return new Promise((success) => {
                self.frame = requestFrame(success);
            });
        });
    }
    /**
     * @function appendDisposeClasses
     * @since 1.0.0
     * @hidden
     */
    function appendDisposeClasses(node) {
        appendClass(node, disposeClassName);
    }
    /**
     * @function removeDisposeClasses
     * @since 1.0.0
     * @hidden
     */
    function removeDisposeClasses(node) {
        removeClass(node, disposeClassName);
    }
    /**
     * @function appendPresentClasses
     * @since 1.0.0
     * @hidden
     */
    function appendPresentClasses(node) {
        appendClass(node, presentClassName);
        appendClass(node, presentClassName + '-active', true);
    }
    /**
     * @function removePresentClasses
     * @since 1.0.0
     * @hidden
     */
    function removePresentClasses(node) {
        removeClass(node, presentClassName);
        removeClass(node, presentClassName + '-active');
    }
    /**
     * @function appendDismissClasses
     * @since 1.0.0
     * @hidden
     */
    function appendDismissClasses(node) {
        appendClass(node, dismissClassName);
        appendClass(node, dismissClassName + '-active', true);
    }
    /**
     * @function removeDismissClasses
     * @since 1.0.0
     * @hidden
     */
    function removeDismissClasses(node) {
        removeClass(node, dismissClassName);
        removeClass(node, dismissClassName + '-active');
    }
    /**
     * @function getNode
     * @since 1.0.0
     * @hidden
     */
    function getNode(index) {
        return self.nodes[index] && getDOMNode(self.nodes[index]);
    }
    /**
     * @function render
     * @since 1.0.0
     * @hidden
     */
    function render(stack) {
        return Object.values(stack);
    }
    useEffect(() => {
        return () => {
            if (self.frame) {
                self.frame = cancelFrame(self.frame);
            }
        };
    }, []);
    if (ref) {
        ref.current = {
            present,
            dismiss
        };
    }
    return render(stack);
});
/**
 * @component PresentStack.Item
 * @since 1.0.0
 * @hidden
 */
class Item extends React.Component {
    /**
     * @inherited
     * @method render
     * @since 1.0.0
     */
    render() {
        return this.props.item;
    }
}
/**
 * @method requestFrame
 * @since 1.0.0
 * @hidden
 */
function requestFrame(callback) {
    return requestAnimationFrame(callback);
}
/**
 * @method cancelFrame
 * @since 1.0.0
 * @hidden
 */
function cancelFrame(frame) {
    cancelAnimationFrame(frame);
    return 0;
}
/**
 * @method appendClass
 * @since 1.0.0
 * @hidden
 */
export function appendClass(node, name, reflow = false) {
    if (node.classList.contains(name)) {
        return Promise.resolve();
    }
    if (reflow) {
        return new Promise((success) => {
            requestAnimationFrame(() => {
                node.scrollTop;
                node.classList.add(name);
                success();
            });
        });
    }
    node.classList.add(name);
    return Promise.resolve();
}
/**
 * @method removeClass
 * @since 1.0.0
 * @hidden
 */
export function removeClass(node, name, reflow = false) {
    if (node.classList.contains(name) == false) {
        return Promise.resolve();
    }
    if (reflow) {
        return new Promise((success) => {
            requestAnimationFrame(() => {
                node.scrollTop;
                node.classList.remove(name);
                success();
            });
        });
    }
    node.classList.remove(name);
    return Promise.resolve();
}
/**
 * @function insert
 * @since 1.0.0
 */
export function insert(dictionary, key, value) {
    const copy = Object.assign({}, dictionary);
    copy[key] = value;
    return copy;
}
/**
 * @function remove
 * @since 1.0.0
 */
export function remove(dictionary, key) {
    const copy = Object.assign({}, dictionary);
    copy[key] = null;
    return copy;
}
